import {
  ChildFragmentFragment,
  ChildInput,
} from "@outschool/gql-frontend-generated";
import { useTrackLearnerCreated } from "@outschool/ui-auth";
import { getRandomAvatar } from "@outschool/ui-components-shared";
import { useCallback, useMemo, useState } from "react";

import {
  useAddChildMutation,
  useUpdateChildMutation,
} from "../../../hooks/Learners";
import { getLabelFromValue } from "../../loggedInHome/learnerCards/utils";
import {
  ClassSizeLearnerAttribute,
  SCHOOLING_APPROACH_SELECTIONS,
} from "../../onboarding/consts";

type ChildInputWithoutInterests = Omit<ChildInput, "interestTopics"> & {};

interface UiState {
  isModalOpen: boolean;
  showPickAvatarModal: boolean;
  expandedAccordion: string | null;
  error: string | undefined;
}

interface ChildState {
  child: ChildInputWithoutInterests;
  childNameHasChanged: boolean;
}

interface PreferencesState {
  childSubjects: Array<string>;
  childNeeds: Array<string>;
  childFormats: Array<string>;
  childClassSizes: Array<Exclude<ClassSizeLearnerAttribute, "group">>;
  schoolingApproach: string;
  otherAttributes: Array<{ key: string; value: string }>;
}

export function useUpsertLearner() {
  // UI state
  const [uiState, setUiState] = useState<UiState>({
    isModalOpen: false,
    showPickAvatarModal: false,
    expandedAccordion: null,
    error: undefined,
  });

  // Child basic information
  const [childState, setChildState] = useState<ChildState>({
    child: {},
    childNameHasChanged: false,
  });

  // Child preferences and attributes
  const [preferencesState, setPreferencesState] = useState<PreferencesState>({
    childSubjects: [],
    childNeeds: [],
    childFormats: [],
    childClassSizes: [],
    schoolingApproach: "",
    otherAttributes: [],
  });

  function handleChangeAccordion(panel: string, isExpanded: boolean) {
    setUiState(prev => ({
      ...prev,
      expandedAccordion: isExpanded ? panel : null,
    }));
  }

  const openModal = useCallback(() => {
    setUiState(prev => ({ ...prev, isModalOpen: true }));
    setChildState(prev => ({
      ...prev,
      child: { avatar: getRandomAvatar() },
    }));
  }, []);

  const closeModal = useCallback(() => {
    setUiState({
      isModalOpen: false,
      showPickAvatarModal: false,
      expandedAccordion: null,
      error: undefined,
    });
    setChildState({
      child: {},
      childNameHasChanged: false,
    });
    setPreferencesState({
      childSubjects: [],
      childNeeds: [],
      childFormats: [],
      childClassSizes: [] as Exclude<ClassSizeLearnerAttribute, "group">[],
      schoolingApproach: "",
      otherAttributes: [],
    });
  }, []);

  const { updateChild, loading: updateLoading } = useUpdateChildMutation();
  const { addChild, loading: addLoading } = useAddChildMutation();

  const trackLearnerCreated = useTrackLearnerCreated();

  const schoolingApproachText = useMemo(() => {
    if (
      preferencesState.schoolingApproach &&
      preferencesState.schoolingApproach.length > 0
    ) {
      return getLabelFromValue({
        value: preferencesState.schoolingApproach,
        selections: SCHOOLING_APPROACH_SELECTIONS,
      });
    }
    return "Choose approach";
  }, [preferencesState.schoolingApproach]);

  const upsertChild = useCallback(async () => {
    // check if we have the minimum child attributes
    if (
      !childState.child.name ||
      !childState.child.birthMonth ||
      !childState.child.birthYear
    ) {
      // can't create child without these
      return undefined;
    }

    const attributes = [
      ...preferencesState.otherAttributes,
      ...(preferencesState.schoolingApproach
        ? [
            {
              key: "schooling_approach",
              value: preferencesState.schoolingApproach,
            },
          ]
        : []),
      ...preferencesState.childSubjects.map(a => ({
        key: "subject_interest",
        value: a,
      })),
      ...preferencesState.childNeeds.map(a => ({
        key: "learning_need",
        value: a,
      })),
      ...preferencesState.childFormats.map(a => ({
        key: "class_format",
        value: a,
      })),
      ...preferencesState.childClassSizes.map(a => ({
        key: "class_size",
        value: a,
      })),
    ];

    const upsertChildData = {
      ...childState.child,
      attributes,
    };

    try {
      let resultChild: ChildFragmentFragment | undefined;
      if (childState.child.uid) {
        const result = await updateChild(upsertChildData);
        resultChild = result.data?.updateChild;
      } else {
        const result = await addChild(upsertChildData);
        resultChild = result.data?.addChild;
        trackLearnerCreated();
      }
      closeModal();
      return resultChild;
    } catch (err) {
      setUiState(prev => ({ ...prev, error: err }));
      return undefined;
    }
  }, [
    childState.child,
    preferencesState,
    addChild,
    updateChild,
    closeModal,
    trackLearnerCreated,
  ]);

  const isLoading = updateLoading || addLoading;

  // Helper functions for updating grouped state
  const updateUiState = useCallback((updates: Partial<UiState>) => {
    setUiState(prev => ({ ...prev, ...updates }));
  }, []);

  const updateChildState = useCallback((updates: Partial<ChildState>) => {
    setChildState(prev => ({ ...prev, ...updates }));
  }, []);

  const updatePreferencesState = useCallback(
    (updates: Partial<PreferencesState>) => {
      setPreferencesState(prev => ({ ...prev, ...updates }));
    },
    [],
  );

  return {
    // Grouped state
    uiState,
    childState,
    preferencesState,

    // State updaters
    updateUiState,
    updateChildState,
    updatePreferencesState,

    // Functions
    handleChangeAccordion,
    openModal,
    closeModal,
    upsertChild,
    schoolingApproachText,
    isLoading,
  };
}
